import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

interface GuidanceContextValue {
  isGuidanceActive: boolean;
  toggleGuidance: () => void;
  setGuidance: (active: boolean) => void;
}

const GuidanceContext = createContext<GuidanceContextValue | null>(null);

const STORAGE_KEY = "iso-clarity-guidance";

const readStored = (): boolean => {
  if (typeof window === "undefined") return true;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === null) return true; // default ON
  return raw === "true";
};

export function GuidanceProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const [isGuidanceActive, setIsGuidanceActive] = useState<boolean>(() => readStored());

  // Persist locally on every change for instant first-paint on reload.
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, String(isGuidanceActive));
    }
  }, [isGuidanceActive]);

  // Reconcile from server on auth.
  useEffect(() => {
    if (status !== "authenticated") return;
    void api<{ preferences: { isGuidanceActive: boolean } }>("/me/preferences")
      .then((res) => setIsGuidanceActive(res.preferences.isGuidanceActive))
      .catch(() => undefined);
  }, [status]);

  const writeThrough = useCallback((next: boolean) => {
    void api("/me/preferences", { method: "PATCH", body: { isGuidanceActive: next } }).catch(
      () => undefined,
    );
  }, []);

  const toggleGuidance = useCallback(() => {
    setIsGuidanceActive((v) => {
      const next = !v;
      writeThrough(next);
      return next;
    });
  }, [writeThrough]);

  const setGuidance = useCallback(
    (active: boolean) => {
      setIsGuidanceActive(active);
      writeThrough(active);
    },
    [writeThrough],
  );

  const value = useMemo<GuidanceContextValue>(
    () => ({ isGuidanceActive, toggleGuidance, setGuidance }),
    [isGuidanceActive, toggleGuidance, setGuidance],
  );

  return <GuidanceContext.Provider value={value}>{children}</GuidanceContext.Provider>;
}

export function useGuidance(): GuidanceContextValue {
  const ctx = useContext(GuidanceContext);
  if (!ctx) throw new Error("useGuidance must be used within a GuidanceProvider");
  return ctx;
}
